import React, { useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import localeText from "../../../../../locale";
import { usePayment } from "../../Checkout/context/PaymentContext";
import { useWalletPayment } from "../../Checkout/hooks/useWalletPayment";
import { formatPrice } from "../../../../../utilities/formatPrice";

const SinglePaymentWallet = ({
  currencyProduct,
  currencyFormat,
  walletBalanceValue,
  combinePayment,
  totalPrice,
  isInCombinedAccordion = false,
}) => {
    const {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    setAmountWallet,
    isPaymentMethodAvailable,
    isTotalCovered,
    isPaymentMethodInUse
  } = usePayment();
  const { amountWallet, maxAmount, handleAmountChange, clearWalletPayment } = useWalletPayment();

  useEffect(() => {
    if (!combinePayment && selectedPaymentMethod !== "wallet") {
      if (amountWallet !== 0) {
        clearWalletPayment();
      }
    }
  }, [selectedPaymentMethod, clearWalletPayment, combinePayment]);

  useEffect(() => {
    if (!combinePayment && selectedPaymentMethod === "wallet" && !isInCombinedAccordion) {
      setAmountWallet(totalPrice);
    }
  }, [
    selectedPaymentMethod,
    combinePayment,
    totalPrice,
    isInCombinedAccordion,
  ]);

  const hasWalletBalance = walletBalanceValue > 0;
  const hasEnoughWalletBalance = walletBalanceValue >= totalPrice;
  const isAvailable = isPaymentMethodAvailable('wallet');
  const totalCovered = isTotalCovered();
  const isInUse = isPaymentMethodInUse('wallet');

  if (combinePayment && totalCovered && !isInUse) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-blue-50 border-round border-blue-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-info-circle text-blue-600"></i>
                         <span className="text-blue-800 font-medium">
               {localeText("totalAlreadyCovered")}
             </span>
           </div>
           <small className="text-blue-700 mt-1 block">
             {localeText("paymentCompleteWithOtherMethodsExplanation")}
           </small>
        </div>
      </div>
    );
  }

  if (combinePayment && !isAvailable) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-gray-50 border-round border-gray-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-ban text-gray-600"></i>
                         <span className="text-gray-800 font-medium">
               {localeText("methodNotAvailable")}
             </span>
           </div>
           <small className="text-gray-700 mt-1 block">
             {localeText("methodNotAvailableExplanation")}
           </small>
        </div>
      </div>
    );
  }

  if (!hasWalletBalance) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-yellow-50 border-round border-yellow-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-yellow-600"></i>
            <span className="text-yellow-800 font-medium">
              {localeText("noWalletBalanceAvailable")}
            </span>
          </div>
          <small className="text-yellow-700 mt-1 block">
            {localeText("walletBalanceRequired")}
          </small>
        </div>
      </div>
    );
  }

  if (!combinePayment && !hasEnoughWalletBalance) {
    return (
      <div className="flex flex-column gap-2">
        <div className="p-3 bg-orange-50 border-round border-orange-200">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-orange-600"></i>
            <span className="text-orange-800 font-medium">
              {localeText("insufficientWalletBalance")}
            </span>
          </div>
          <small className="text-orange-700 mt-1 block">
            {localeText("insufficientWalletBalanceMessage")
              .replace("{currency}", currencyFormat[currencyProduct])
              .replace("{amount}", formatPrice(walletBalanceValue))
              .replace("{total}", formatPrice(parseFloat((Number(totalPrice) || 0).toFixed(2))))}
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-column">
      <label htmlFor="wallet-amount" className="font-medium">
        {localeText("amountToPay")}
      </label>
      <InputNumber
        id="wallet-amount"
        value={combinePayment || isInCombinedAccordion ? amountWallet : totalPrice}
        onValueChange={(e) => handleAmountChange(e.value, combinePayment, totalPrice)}
        mode="currency"
        currency={currencyProduct}
        locale="en-US"
        min={0}
        max={combinePayment || isInCombinedAccordion ? maxAmount : totalPrice}
        className="w-full mb-1"
        disabled={(!combinePayment && !isInCombinedAccordion) || (combinePayment && !isAvailable)}
      />
      <small className="text-color-secondary">
        {localeText("walletAmountDescription")}
      </small>
    </div>
  );
};

export default SinglePaymentWallet;
